/**
 * Date-time formatting for the admin, on `Intl` instead of moment (SCRUM-1617).
 *
 * moment was down to three `.format()` calls in the admin, and one of them
 * rendered the wrong thing: `"d MMM hh:mm"` prints the *weekday number* (`d`,
 * 0–6) where the editor reads a day of the month, and a 12-hour clock without
 * an AM/PM token. Both helpers below are pinned to the `en` locale moment used
 * to be fixed on, and both format in the browser's local zone, as moment did.
 *
 * Difference kept on purpose: `moment(undefined)` is "now", so a missing
 * timestamp used to render today's date. Here it renders `Invalid date` — the
 * same string moment printed for an unparseable one — so a missing field is
 * visible rather than disguised as a fresh edit.
 */

const INVALID = "Invalid date";

const toDate = (value: unknown): Date | null => {
  if (value === undefined || value === null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value as any);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dateTimeFormat = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
});

const dayMonthTimeFormat = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/**
 * `Sep 2, 2026, 3:15 PM` — moment's `lll` with Intl's comma before the time.
 * @param value ISO string, epoch or Date
 */
export function formatDateTime(value: unknown): string {
  const d = toDate(value);
  return d ? dateTimeFormat.format(d) : INVALID;
}

/**
 * `2 Sep 15:15` — day of month, short month, 24-hour clock; what the publish
 * history's `"d MMM hh:mm"` was meant to be.
 * @param value ISO string, epoch or Date
 */
export function formatDayMonthTime(value: unknown): string {
  const d = toDate(value);
  if (!d) return INVALID;
  const parts = Object.fromEntries(
    dayMonthTimeFormat
      .formatToParts(d)
      .filter((p) => p.type !== "literal")
      .map((p) => [p.type, p.value])
  );
  return `${parts.day} ${parts.month} ${parts.hour}:${parts.minute}`;
}
